import { Suspense, useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { Route, Routes, BrowserRouter as Router, useLocation } from "react-router-dom";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import * as Sentry from "@sentry/browser";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ErrorBoundary } from "react-error-boundary";
import { CssBaseline } from "@mui/material";
import { Auth0Provider } from '@auth0/auth0-react';
import { shallow } from "zustand/shallow";

import "./index.scss";
import "react-table-6/react-table.css";
import { theme } from "./theme.js";

import useGlobalState from "./use-global-state.js";
// Components
import ErrorFallback from "./components/ErrorFallback.js";
import { SuspenseSpinner } from "./tctt.js";
import Footer from "./components/Footer.js"
import GuestOnly from "./components/GuestOnly.js";
import Header from "./components/Header.js";
import Protected from "./components/Protected.js";
import Snackbar from "./components/Snackbar.js";

// Screens
import Auth from "./screens/Auth.js";
import Collaboration from "./screens/Collaboration.js";
import DslModelScreen from "./screens/DslModelScreen.js";
import DslScreen from "./screens/DslScreen.js";
import Dsls from "./screens/Dsls.js";
import Examples from "./screens/Examples.js";
import Home from "./screens/Home.js";
import NotFound from "./screens/NotFound.js";
import ProjectScreen from "./screens/ProjectScreen.js";
import Projects from "./screens/Projects.js";
import Secrets from "./screens/Secrets.js";
import SignIn from "./screens/SignIn.js";
import Testbed from "./screens/Testbed.js";
import Unauthorized from "./screens/Unauthorized.js";
import Marketplace from "./screens/Marketplace.js";
import Admin from "./screens/Admin.js";
import GrammarGen from "./screens/GrammarGen.js";
import AssistantScreen from "./screens/AssistantScreen.js";

import { jwt } from "./utils/index.js";

import { Provider } from 'react-redux';
import store from "./components/AppCreator/store.js";

import "./i18n.js";

const {
	NODE_ENV,
	REACT_APP_SENTRY_DSN,
	REACT_APP_SENTRY_ENVIRONMENT,
	REACT_APP_AUTH0_DOMAIN,
	REACT_APP_AUTH0_CLIENT_ID,
} = process.env;

Sentry.init({
	dsn: REACT_APP_SENTRY_DSN,
	environment: REACT_APP_SENTRY_ENVIRONMENT,
	ignoreErrors: [
		"ResizeObserver loop limit exceeded",
		"Non-Error promise rejection captured",
	],
	enabled: NODE_ENV === "production",
});

const App = () => {
	const location = useLocation();
	const [authenticated, setAuthenticated] = useState(false);

	const { maxDSLScreen } = useGlobalState((e) => ({
		maxDSLScreen: e.maxDSLScreen,
	}), shallow);

	useEffect(() => {
		setAuthenticated(jwt.isAuthenticated());
	}, [location]);

	return (
		<StyledEngineProvider injectFirst>
			<CssBaseline />
			<ThemeProvider theme={theme}>
				<ErrorBoundary FallbackComponent={ErrorFallback}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<Header isAuthenticated={authenticated} />
						<main style={{ position: "relative", zIndex: 0, height: `calc(100vh - ${maxDSLScreen ? 0 : (authenticated ? "160" : "70")}px)` }}>
							<Routes>
								<Route index element={<GuestOnly c={<SignIn />} />} />
								<Route path="auth" element={<GuestOnly c={<Auth />} />} />
								<Route path="home" element={<Protected c={<Home />} />} />
								<Route path="examples" element={<Protected c={<Examples />} />} />

								{/* Dsls general */}
								<Route path="dsls" element={<Protected c={<Dsls />} />} />
								<Route path="projects" element={<Protected c={<Projects />} />} />
								<Route path="projects/:projectid" element={<Protected c={<ProjectScreen />} />} />
								<Route path="secrets" element={<Protected c={<Secrets />} />} />
								<Route path="collaboration" element={<Protected c={<Collaboration />} />} />
								<Route path="marketplace" element={<Protected c={<Marketplace />} />} />

								{/* Admin Panel */}
								<Route path="admin/:content" element={<Protected c={<Admin />} />} />

								{/* Grammar Generation */}
								<Route path="generate-grammar" element={<Protected c={<GrammarGen />} />} />

								{/* Assistant Screen */}
								<Route path="assistant" element={<Protected c={<AssistantScreen />} />} />

								{/* DSLs */}
								<Route path="dsls/envmaker" element={<Protected c={<DslScreen dsl="envmaker" />} />} />
								<Route path="dsls/envmaker/:modelid" element={<Protected c={<DslModelScreen dsl="envmaker" />} />} />
								<Route path="dsls/envpop" element={<Protected c={<DslScreen dsl="envpop" />} />} />
								<Route path="dsls/envpop/:modelid" element={<Protected c={<DslModelScreen dsl="envpop" />} />} />
								<Route path="dsls/smauto" element={<Protected c={<DslScreen dsl="smauto" />} />} />
								<Route path="dsls/smauto/:modelid" element={<Protected c={<DslModelScreen dsl="smauto" />} />} />
								<Route path="dsls/codintxt" element={<Protected c={<DslScreen dsl="codintxt" />} />} />
								<Route path="dsls/codintxt/:modelid" element={<Protected c={<DslModelScreen dsl="codintxt" />} />} />
								<Route path="dsls/openapi" element={<Protected c={<DslScreen dsl="openapi" />} />} />
								<Route path="dsls/openapi/:modelid" element={<Protected c={<DslModelScreen dsl="openapi" />} />} />
								<Route path="dsls/demol" element={<Protected c={<DslScreen dsl="demol" />} />} />
								<Route path="dsls/demol/:modelid" element={<Protected c={<DslModelScreen dsl="demol" />} />} />
								<Route path="dsls/xmasdsl" element={<Protected c={<DslScreen dsl="xmasdsl" />} />} />
								<Route path="dsls/xmasdsl/:modelid" element={<Protected c={<DslModelScreen dsl="xmasdsl" />} />} />
								<Route path="dsls/goaldsl" element={<Protected c={<DslScreen dsl="goaldsl" />} />} />
								<Route path="dsls/goaldsl/:modelid" element={<Protected c={<DslModelScreen dsl="goaldsl" />} />} />
								<Route path="dsls/cpsml" element={<Protected c={<DslScreen dsl="cpsml" />} />} />
								<Route path="dsls/cpsml/:modelid" element={<Protected c={<DslModelScreen dsl="cpsml" />} />} />
								<Route path="dsls/generos" element={<Protected c={<DslScreen dsl="generos" />} />} />
								<Route path="dsls/generos/:modelid" element={<Protected c={<DslModelScreen dsl="generos" />} />} />
								<Route path="dsls/commidl" element={<Protected c={<DslScreen dsl="commidl" />} />} />
								<Route path="dsls/commidl/:modelid" element={<Protected c={<DslModelScreen dsl="commidl" />} />} />
								<Route path="dsls/toolboxer" element={<Protected c={<DslScreen dsl="toolboxer" />} />} />
								<Route path="dsls/toolboxer/:modelid" element={<Protected c={<DslModelScreen dsl="toolboxer" />} />} />
								<Route path="dsls/appcreator" element={<Protected c={<DslScreen dsl="appcreator" />} />} />
								<Route path="dsls/appcreator/:modelid" element={<Protected c={<DslModelScreen dsl="appcreator" />} />} />
								<Route path="dsls/dflow" element={<Protected c={<DslScreen dsl="dflow" />} />} />
								<Route path="dsls/dflow/:modelid" element={<Protected c={<DslModelScreen dsl="dflow" />} />} />
								<Route path="dsls/pseudodsl" element={<Protected c={<DslScreen dsl="pseudodsl" />} />} />
								<Route path="dsls/pseudodsl/:modelid" element={<Protected c={<DslModelScreen dsl="pseudodsl" />} />} />
								<Route path="dsls/webdsl" element={<Protected c={<DslScreen dsl="webdsl" />} />} />
								<Route path="dsls/webdsl/:modelid" element={<Protected c={<DslModelScreen dsl="webdsl" />} />} />
								<Route path="dsls/homeassistantdsl" element={<Protected c={<DslScreen dsl="homeassistantdsl" />} />} />
								<Route path="dsls/homeassistantdsl/:modelid" element={<Protected c={<DslModelScreen dsl="homeassistantdsl" />} />} />
								<Route path="dsls/eml" element={<Protected c={<DslScreen dsl="eml" />} />} />
								<Route path="dsls/eml/:modelid" element={<Protected c={<DslModelScreen dsl="eml" />} />} />
								<Route path="dsls/json" element={<Protected c={<DslScreen dsl="json" />} />} />
								<Route path="dsls/json/:modelid" element={<Protected c={<DslModelScreen dsl="json" />} />} />
								<Route path="dsls/robocon" element={<Protected c={<DslScreen dsl="robocon" />} />} />
								<Route path="dsls/robocon/:modelid" element={<Protected c={<DslModelScreen dsl="robocon" />} />} />



								<Route path="testbed" element={<Protected c={<Testbed />} />} />
								<Route path="*" element={<NotFound />} />
								<Route path="/unauthorized" element={<Unauthorized />} />
							</Routes>
						</main>
						{authenticated && <Footer />}
						<Snackbar />
					</LocalizationProvider>
				</ErrorBoundary>
			</ThemeProvider>
		</StyledEngineProvider>
	);
};

const root = ReactDOM.createRoot(document.querySelector("#root"));
root.render(
	<Suspense fallback={<SuspenseSpinner />}>
		<Provider store={store}>
			<Auth0Provider
				domain={REACT_APP_AUTH0_DOMAIN}
				clientId={REACT_APP_AUTH0_CLIENT_ID}
				authorizationParams={{
					redirect_uri: window.location.origin,
				}}
			>
				<Router>
					<App />
				</Router>
			</Auth0Provider>
		</Provider>
	</Suspense>
);
